/*
 * Single data seam for every screen. There is NO mock/demo source anywhere
 * (Duncan directive BOARDROOM-2026-08-04-084237): a signed-in user sees REAL
 * stage data or an honest loading/empty/unavailable state, full stop.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { loadSession, clearSession, storeSession, type Session } from '../auth/session'
import { refreshSession } from '../auth/cognitoAuth'
import { ApiAuthError, fetchConsumerOrders, ticketsApiBase, type LiveData, type LiveDataState } from './api'
import { fetchOrgBranding, isOrgUnitUuid, orgBrandingApiBase, type OrgBrandingResolved } from './orgBranding'
import type { Order, Ticket, TrEvent } from './types'

interface DataContextValue {
  liveState: LiveDataState
  orders: Order[]
  tickets: Ticket[]
  events: TrEvent[]
  eventById: (id: string) => TrEvent | undefined
  ticketsForOrder: (orderId: string) => Ticket[]
  /** Resolved org-unit branding (settings' central-asset-store read,
   *  BOARDROOM-2026-08-28-110128). undefined = not (yet) fetched;
   *  null = org unit unknown (404). Best-effort — consumers keep the
   *  neutral fallback on undefined/null and on any null field. */
  orgBranding: (orgUnitId: string) => OrgBrandingResolved | null | undefined
  sessionEmail?: string
  refresh: () => void
}

const DataContext = createContext<DataContextValue | null>(null)

/**
 * BOARDROOM-105741 — self-healing reauth. On a 401 the OLD code reloaded the
 * page on the SAME stale id token (gated by a 15s throttle), which left a user
 * with an expired token — or a token minted before a server-side authorizer fix
 * — PERMANENTLY on the "unavailable" holding card with no clean recovery.
 *
 * Instead: exchange the stored refresh token for a FRESH session (Cognito
 * REFRESH_TOKEN_AUTH — re-mints the id token from the account's current state),
 * persist it, and RE-FETCH orders with the new id token. Returns the orders on
 * success; null when there is no refresh token, the refresh fails, or the FRESH
 * token is ALSO rejected (a genuine authorizer reject, not a stale token) — the
 * caller then drops to a clean re-login. A normal token expiry now self-heals
 * with no page reload.
 */
async function tryRefreshAndRefetch(session: Session, base: string): Promise<LiveData | null> {
  if (!session.refreshToken) return null
  const username =
    (session.claims['cognito:username'] as string | undefined) ||
    session.claims.email ||
    session.claims.sub
  try {
    const fresh = await refreshSession(username, session.refreshToken)
    storeSession(fresh) // persist so the whole app uses the fresh tokens
    return await fetchConsumerOrders(fresh.idToken, base)
  } catch {
    return null
  }
}

export function DataProvider({ children }: { children: ReactNode }) {
  const session = loadSession()
  const [liveState, setLiveState] = useState<LiveDataState>('unconfigured')
  const [live, setLive] = useState<LiveData>({ orders: [], tickets: [], events: [] })
  const [branding, setBranding] = useState<Record<string, OrgBrandingResolved | null>>({})
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!session) return
    const base = ticketsApiBase()
    if (!base) {
      setLiveState('unconfigured')
      return
    }
    let cancelled = false
    setLiveState('loading')

    const load = async () => {
      try {
        const data = await fetchConsumerOrders(session.idToken, base)
        if (cancelled) return
        setLive(data)
        setLiveState('ready')
      } catch (err) {
        if (cancelled) return
        // 401/403 = the id token was rejected (expired, or minted before a
        // server-side authorizer fix). Do NOT reload on the same stale token
        // (BOARDROOM-105741) — first REFRESH via the Cognito refresh token and
        // re-fetch with the fresh id token. A normal expiry self-heals silently.
        if (err instanceof ApiAuthError) {
          const recovered = await tryRefreshAndRefetch(session, base)
          if (cancelled) return
          if (recovered) {
            setLive(recovered)
            setLiveState('ready')
            return
          }
          // Refresh unavailable/failed OR the fresh token is also rejected →
          // clean re-login (clear + reload the auth gate). Throttled so a
          // persistently-rejecting token can't loop the page; inside the window
          // we show the honest holding state instead of a reload storm.
          const KEY = 'mytrumin.reauth.at'
          const last = Number(sessionStorage.getItem(KEY) || 0)
          if (Date.now() - last > 15000) {
            sessionStorage.setItem(KEY, String(Date.now()))
            clearSession()
            window.location.reload()
            return
          }
          setLiveState('unavailable')
          return
        }
        setLiveState('unavailable')
      }
    }
    void load()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick])

  // Best-effort org-unit branding (BOARDROOM-2026-08-28-110128): for each
  // distinct REAL org-unit id on the loaded orders, read settings' resolved
  // branding once. Synthesised `org-{name}` fallback ids are never sent (the
  // endpoint 400s on non-UUIDs), fetch failures leave the neutral fallback in
  // place, and a 404 is cached as null so it is not re-fetched every render.
  useEffect(() => {
    if (!session) return
    const base = orgBrandingApiBase()
    if (!base) return
    const wanted = [...new Set(live.events.map((e) => e.organiser.id))].filter(
      (id) => isOrgUnitUuid(id) && !(id in branding),
    )
    if (wanted.length === 0) return
    let cancelled = false
    void Promise.all(
      wanted.map(async (id) => {
        try {
          return [id, await fetchOrgBranding(id, base, session.idToken)] as const
        } catch {
          return null // transient failure — leave unfetched so a later load retries
        }
      }),
    ).then((pairs) => {
      if (cancelled) return
      const landed = pairs.filter((p): p is readonly [string, OrgBrandingResolved | null] => p !== null)
      if (landed.length) setBranding((prev) => ({ ...prev, ...Object.fromEntries(landed) }))
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live.events])

  // Apply the resolved accent as the organiser identity colour. Events without
  // a resolved accent keep accentFg '' — consumers render the neutral fallback
  // (never a status hue, README §Status colours).
  const events = useMemo<TrEvent[]>(
    () =>
      live.events.map((e) => {
        const accent = branding[e.organiser.id]?.organiserAccent
        return accent ? { ...e, organiser: { ...e.organiser, accentFg: accent } } : e
      }),
    [live.events, branding],
  )

  const value = useMemo<DataContextValue>(() => ({
    liveState,
    orders: live.orders,
    tickets: live.tickets,
    events,
    eventById: (id) => events.find((e) => e.id === id),
    ticketsForOrder: (orderId) => live.tickets.filter((t) => t.orderId === orderId),
    orgBranding: (orgUnitId) => branding[orgUnitId],
    sessionEmail: session?.claims.email,
    refresh: () => setTick((t) => t + 1),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [liveState, live, events, branding])

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export function useData(): DataContextValue {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useData outside DataProvider')
  return ctx
}

/** Optional reader — null outside a provider (component tests / isolated flow
 *  mounts), mirroring useGetReadyLiveOptional. Callers degrade to their
 *  neutral fallback instead of crashing (V1.9.1 P4: the waiver flow's
 *  organiser-accent read). */
export function useDataOptional(): DataContextValue | null {
  return useContext(DataContext)
}

/** Honest status panel while the backing service is loading or absent. */
export function LiveDataNotice({ liveState }: { liveState: LiveDataState }) {
  // BOARDROOM-2026-08-15-090842: env-AGNOSTIC copy — these strings render on PROD,
  // so they must never say "stage" (that read wrong + unprofessional to Duncan's
  // prod test). No environment word at all; the state is what matters.
  const message =
    liveState === 'loading'
      ? 'Loading your orders…'
      : liveState === 'unavailable'
        ? 'Your orders are temporarily unavailable — they will appear the moment the service is back.'
        : 'The orders service is not connected in this build — your real orders appear the moment it is.'
  return (
    <div role="status" data-wired="false" style={{ border: '1px dashed var(--text-faint)', borderRadius: 'var(--r-card)', padding: 24, color: 'var(--text-secondary)', fontSize: 13, marginTop: 12 }}>
      {message}
    </div>
  )
}
